import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import Dimmer from '~/app/common/Dimmer';
import { useSwipeable } from 'react-swipeable';

import classes from './style.module.scss';
import ReactDOM from 'react-dom';
import Icon from "~/app/common/Icon";
import ConfirmationDialog from '~/app/modules/imageEditor/ConfirmationDialog';
import useTranslation from '~/app/hooks/useTranslation';
import Loader from '../Loader';
import { notify } from '~/app/helpers/notify';
import { downloadAttachment } from '~/app/common/Attachment/helper';

export interface LightBoxProps {
    open?: boolean
    edit?: boolean
    remove?: boolean
    download?: boolean
    loading?: boolean
    onClose?: () => void
    onEdit?: () => void
    onRemove?: () => void
    className?: string
    imageUrl?: string | null
    images?: string[]
    initialIndex?: number
    avatarText?: string
}

export type LightBoxComponent = React.FC<LightBoxProps>

const LightBox: LightBoxComponent = ({
    imageUrl = null,
    images,
    initialIndex = 0,
    open = false,
    edit = false,
    remove = false,
    download = false,
    loading = false,
    onClose,
    onEdit,
    onRemove,
    avatarText = '',
    className = '',
}) => {
    const urls = images?.length ? images : (imageUrl ? [imageUrl] : []);
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const { t } = useTranslation('user');
    const currentImage = urls[currentIndex] ?? null;
    const hasGallery = urls.length > 1;

    useEffect(() => {
        if (open) {
            const clamped = Math.max(0, Math.min(initialIndex, Math.max(urls.length - 1, 0)));
            setCurrentIndex(clamped);
        }
    }, [open, initialIndex, urls.length]);

    const handleDeleteImage = useCallback(() => {
        setIsConfirmationOpen(false);
        onRemove?.();
    }, [onRemove]);

    const goToPrevious = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    }, []);

    const goToNext = useCallback((e?: React.MouseEvent | React.TouchEvent) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev < urls.length - 1 ? prev + 1 : prev));
    }, [urls.length]);

    const swipeHandlers = useSwipeable({
        onSwipedLeft: () => {
            if (hasGallery) {
                setCurrentIndex((prev) => (prev < urls.length - 1 ? prev + 1 : prev));
            }
        },
        onSwipedRight: () => {
            if (hasGallery) {
                setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
            }
        },
        trackMouse: true,
    });

    const handleDownload = useCallback(async () => {
        if (!currentImage || isDownloading) return;

        setIsDownloading(true);
        try {
            await downloadAttachment(currentImage);
            notify(
                t(window['cordova']?.platformId === 'ios'
                    ? 'imageDownloadSuccessIos'
                    : 'imageDownloadSuccess'),
                'success'
            );
        } catch {
            notify(t('imageDownloadError'), 'error');
        } finally {
            setIsDownloading(false);
        }
    }, [currentImage, isDownloading, t]);

    return ReactDOM.createPortal(
        <>
            <Dimmer show={open} page>
                <Dimmer.AnimationContent
                    classNames={{
                        enter: classes['enter'],
                        enterActive: classes['enter-active'],
                        exit: classes['exit'],
                        exitActive: classes['exit-active'],
                    }}>
                    <div className={classNames(classes['light-box'], className)} {...swipeHandlers}>
                        {!loading && !isDownloading && (
                            <>
                                {remove && currentImage && (
                                    <Icon name="delete" className={classes['remove']} onClick={() => setIsConfirmationOpen(true)}/>
                                )}
                                {edit && (
                                    <Icon name="edit" className={classes['edit']} onClick={onEdit}/>
                                )}
                                {download && currentImage && (
                                    <Icon name="download" className={classes['download']} onClick={handleDownload}/>
                                )}
                                <Icon name="close" className={classes['close']} onClick={onClose}/>
                            </>
                        )}
                        {(loading || isDownloading) ? (
                            <Loader withDimmer inverse />
                        ) : currentImage ? (
                            <>
                                <img src={currentImage} alt='' className={classes['image']}/>
                                {hasGallery && (
                                    <div className={classes['gallery-nav']}>
                                        <button
                                            type="button"
                                            className={classNames(classes['nav-button'], classes['nav-prev'])}
                                            onClick={goToPrevious}
                                            disabled={currentIndex === 0}
                                            aria-label="Previous image"
                                        >
                                            <Icon name="arrow_back_ios" className={classes['nav-icon']} />
                                        </button>
                                        <span className={classes['gallery-counter']}>
                                            {`(${currentIndex + 1}/${urls.length})`}
                                        </span>
                                        <button
                                            type="button"
                                            className={classNames(classes['nav-button'], classes['nav-next'])}
                                            onClick={goToNext}
                                            disabled={currentIndex === urls.length - 1}
                                            aria-label="Next image"
                                        >
                                            <Icon name="arrow_back_ios" className={classes['nav-icon']} />
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={classes['avatar']}>{avatarText}</div>
                        )}
                    </div>
                </Dimmer.AnimationContent>
            </Dimmer>
            <ConfirmationDialog
                isOpen={isConfirmationOpen}
                onConfirm={handleDeleteImage}
                onCancel={() => setIsConfirmationOpen(false)}
                confirmationText={t('imageRemoveConfirm')}
            />
        </>,
        document.getElementById('root') as HTMLElement);
}

export default LightBox
